import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { Avatar, Box, IconButton, Typography } from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";

import dayjs from "dayjs";
import { UUID } from "crypto";
import { esES } from "@mui/x-data-grid/locales";
import { useDispatch, useSelector } from "react-redux";
import { GridCellParams, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useDialogs } from "@toolpad/core/useDialogs";

import { RootState, store } from "@store/store";
import { LoanAppComments } from "./LoanAppComments";
import { LoanAppChannelInfo } from "./LoanAppChannelInfo";
import { LoanApplicationInfo } from "./LoanApplicationInfo";
import { LoanAppLostInterest } from "./LoanAppLostInterest";
import { formatCurrency, formatHours } from "@helpers/formats";
import { formatFullName } from "@helpers/generals";
import { LoanAppChangeInterest } from "./LoanAppChangeInterest";
import { LoanAppOwnerAssignment } from "./LoanAppOwnerAssignment";
import { EdicionEstatusDialog } from "@features/Solicitud/subFeatures/InfoGeneral/components/EdicionStatusDialog";
import { setApplicationSelected, setCleanApplicationSelected } from "@store/slices/componentsSlice";
import { CustomPopupModal } from "./CustomPopupModal";
import { StripedDataGrid } from "@components/CustomDataGrid";
import { setDownloadingList, setFiltersByCode, setSortsByCode } from "@store/slices/sessionSlice";
import { useSolicitudesResumeQuery } from "../hooks/useSolicitudesResumeQuery";

import VisibilityIcon from "@assets/see_icon.svg";
import Person from "@assets/person_normal_icon.svg";
import SeeOverIcon from "@assets/see_hover_icon.svg";
import CommentIcon from "@assets/empty_comments_icon.svg";
import WithComments from "@assets/with_comments_icon.svg";
import AlertPerson from "@assets/person_lost_interest_icon.svg";
import RecoveryPerson from "@assets/person_recovery_interest_icon.svg";

import "dayjs/locale/es-mx";
import { Link, useNavigate } from "react-router-dom";
import { useGetPermission } from "@hooks/useGetPermission";

interface LoanAppDataGridProps {
    refreshData: boolean;
    cols: Array<string>;
    listCode: string;
}

export const LoanAppDataGrid = (props: LoanAppDataGridProps) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const dialogs = useDialogs();

    const [colorUsers, setColorUsers] = useState(new Map());
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [childrenElement, setChildrenElement] = useState<ReactNode | null>(null);

    const sbdexPermission = useGetPermission("SBDEX");
    const sbpinPermission = useGetPermission("SBPIN");
    const sbcomPermission = useGetPermission("SBCOM");
    const sbingrPermission = useGetPermission("SBINGR");
    const acaesPermission = useGetPermission("ACAES");

    const canEditStatus = acaesPermission?.can_watch === true && acaesPermission?.can_edit === true;

    const session = useSelector((state: RootState) => state.session);
    const downloadingList = useSelector((state: RootState) => state.session.filters.downloadingList);
    const companiesUser = useSelector((state: RootState) => state.session.filters.shared_filters.companies);
    const configList = useSelector((state: RootState) =>
        state.session.filters.configList.find((t) => t.code === props.listCode),
    );

    // const permissions = (state = store.getState()) => state.session.permissions;
    // const financialsUser = (state = store.getState()) => state.session.filters.shared_filters.financials;
    // const currentConfigList = (state = store.getState()) =>
    //     state.session.filters.configList.find((t) => t.code === props.listCode);

    // const sessionPermissions = createSelector([permissions], (permissions) => {
    //     return permissions.filter((permission) => ["SBDEX", "SBPIN", "SBCOM"].includes(permission.module.code));
    // });

    // const permission = useSelector((state: RootState) => state.session.permissionsV2);

    const { rows, setRows, pagination, setPagination, isLoading, refetch, fetchPage } = useSolicitudesResumeQuery(
        props.listCode,
        {
            refreshKey: props.refreshData,
            onBeforeFetch: (reason) => {
                if (reason === "change") dispatch(setCleanApplicationSelected());
                setAnchorEl(null);
            },
        },
    );

    const getColumns = useMemo(() => {
        const canAssignOwner =
            session.user.role?.code === "RADM2" ||
            sbingrPermission?.children.find((t) => t.code === "ACCCA")?.can_watch === true;

        const columnsDef: GridColDef[] = [
            {
                headerName: "Id Solicitud",
                field: "id_application_company",
                type: "number",
                align: "center",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                flex: 1,
                renderCell: (row) => {
                    return (
                        <Link
                            onClick={(event: React.SyntheticEvent<HTMLElement>) => handleOnClickLoanAppId(event, row)}
                        >
                            {row.value}
                        </Link>
                    );
                },
            },
            {
                headerName: "Razón Social / PFAE",
                field: "company_name",
                type: "string",
                align: "left",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                minWidth: 100,
                renderCell: (row) => {
                    return row.value === null ? "-" : row.value;
                },
            },
            {
                headerName: "RFC",
                field: "company_rfc",
                type: "string",
                align: "left",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                minWidth: 120,
                renderCell: (row) => {
                    return row.value === null ? "-" : row.value;
                },
            },
            {
                headerName: "Reg. Fiscal",
                field: "tax_system_type",
                type: "string",
                align: "center",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                minWidth: 50,
                maxWidth: 60,
            },
            {
                headerName: "Nombre Usuario",
                field: "user_name",
                type: "string",
                align: "left",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                minWidth: 120,
                maxWidth: 200,
                flex: 1,
            },
            {
                headerName: "Teléfono Usuario",
                field: "user_phone",
                type: "string",
                align: "left",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                minWidth: 120,
                maxWidth: 200,
                flex: 1,
            },
            {
                headerName: "Correo Usuario",
                field: "user_email",
                type: "string",
                align: "left",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                minWidth: 120,
                maxWidth: 200,
                flex: 1,
            },
            {
                headerName: "Monto",
                field: "amount",
                type: "number",
                align: "center",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                minWidth: 120,
                maxWidth: 130,
                flex: 1,
                renderCell: (row) => {
                    if (row.row.company_source === "PYMIO" && ["CSEOF", "CSOFE"].includes(row.row.status_code)) {
                        return formatCurrency(row.row.requested_amount);
                    } else {
                        return formatCurrency(
                            row.row.authorized ? (row.row.approved_amount ?? 0) : (row.row.requested_amount ?? 0),
                            "$",
                            0,
                        );
                    }
                },
            },
            {
                headerName: "Plazo",
                field: "term",
                type: "number",
                align: "center",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                flex: 1,
                renderCell: (row) => {
                    if (row.row.company_source === "PYMIO" && ["CSEOF", "CSOFE"].includes(row.row.status_code)) {
                        return row.row.requested_term;
                    } else {
                        return row.row.authorized ? (row.row.approved_max_term ?? 0) : (row.row.requested_term ?? 0);
                    }
                },
            },
            {
                headerName: "Fecha Rechazo",
                field: "last_status_date",
                type: "string",
                align: "center",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                flex: 1,
                renderCell: (row) => {
                    return dayjs(row.row.last_status_date).utc(true).local().locale("es-mx").format("DD-MM-YYYY");
                },
            },
            {
                headerName: "Tiempo en Estatus",
                field: "screen_time_hours",
                type: "string",
                align: "center",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                filterable: false,
                flex: 1,
                renderCell: (row) => {
                    return row.value === null ? "-" : formatHours(parseInt(row.value));
                },
            },
            {
                headerName: "Tiempo Total",
                field: "total_time_hours",
                type: "string",
                align: "center",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                filterable: false,
                flex: 1,
                renderCell: (row) => {
                    return formatHours(parseInt(row.value));
                },
            },
            {
                headerName: "Fecha Dispersión",
                field: "disburment_date",
                type: "string",
                align: "center",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                flex: 1,
                renderCell: (row) => {
                    return row.row.disburment_date === null
                        ? "-"
                        : dayjs(row.row.disburment_date).utc(true).local().locale("es-mx").format("DD-MM-YYYY");
                },
            },
            {
                headerName: props.listCode === "MREC" ? "Etapa de Rechazo" : "Etapa",
                field: "stage_name",
                type: "string",
                align: "center",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                flex: 1,
                renderCell: (row) => {
                    const textColor = stageColors.get(row.value);
                    return (
                        <Typography
                            variant="body2"
                            sx={{
                                color: textColor,
                                fontWeight: 700,
                            }}
                        >
                            {row.value}
                        </Typography>
                    );
                },
            },
            {
                headerName: props.listCode === "MREC" ? "Sub Etapa de Rechazo" : "Sub Etapa",
                field: "substage_name",
                type: "string",
                align: "center",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                flex: 1,
            },
            {
                headerName: "Estatus",
                field: "status_name",
                type: "string",
                align: "center",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                flex: 1,
                renderCell: (row) => {
                    if (!canEditStatus) return row.value;

                    return <Box sx={{ cursor: "pointer", userSelect: "none" }}>{row.value}</Box>;
                },
            },
            {
                headerName: "Tiempo Total Proceso",
                field: "client_total_time_hours",
                type: "string",
                align: "center",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                flex: 1,
                renderCell: (row) => {
                    return formatHours(parseInt(row.value));
                },
            },
            {
                headerName: "Macrocanal",
                field: "macrochannel",
                type: "string",
                align: "center",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                flex: 1,
            },
            {
                headerName: "Canal",
                field: "channel",
                type: "string",
                align: "center",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                flex: 1,
                renderCell: (row) => {
                    if (row.value !== "Orgánico") {
                        return (
                            <Link
                                onClick={(event: React.SyntheticEvent<HTMLElement>) =>
                                    handleOnClickLoanAppId(event, row)
                                }
                            >
                                {row.value}
                            </Link>
                        );
                    }
                    return row.value;
                },
            },
            {
                headerName: "Asignación",
                field: "assigned_partner",
                type: "string",
                align: "left",
                headerAlign: "center",
                // minWidth: 180,
                maxWidth: 250,
                flex: 1,
                renderCell: (row) => {
                    if (row.value?.trim().length !== 0) {
                        let initials = "";

                        const names = row.value.split(" ");
                        initials = names.length === 1 ? names[0] : names[0].substring(0, 1) + names[1].substring(0, 1);

                        return (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    marginTop: "0.2em",
                                }}
                            >
                                <Avatar
                                    sx={{ width: 30, height: 30, marginRight: 1, bgcolor: colorUsers.get(row.value) }}
                                    alt={row.value}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                        }}
                                    >
                                        {initials}
                                    </Typography>
                                </Avatar>
                                <Typography
                                    sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                    variant="body2"
                                >
                                    {row.value}
                                </Typography>
                            </Box>
                        );
                    }
                    return row.value;
                },
            },
            {
                headerName: "Compañia",
                field: "company_source",
                type: "string",
                align: "center",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                flex: 1,
            },
            {
                headerName: "Financiera",
                field: "financial_company",
                type: "string",
                align: "center",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                flex: 1,
                renderCell: (row) => {
                    return row.row.financial_company ? row.row.financial_company.name : "";
                },
            },
            {
                headerName: "Producto",
                field: "product_description",
                type: "string",
                align: "center",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                flex: 1,
            },
            {
                headerName: "Comentarios",
                field: "comments",
                align: "center",
                headerAlign: "center",
                flex: 1,
                hideSortIcons: true,
                disableColumnMenu: true,
                disableExport: true,
                disableReorder: true,
                sortable: false,
                renderCell: (row) => {
                    let withComments = false;
                    switch (props.listCode) {
                        case "MSOL":
                            withComments = row.row.msol_total_comments != 0;
                            break;
                        case "MREV":
                            withComments = row.row.mrev_total_comments != 0;
                            break;
                        case "MPIN":
                            withComments = row.row.mpin_total_comments != 0;
                            break;
                        case "MAUT":
                            withComments = row.row.maut_total_comments != 0;
                            break;
                        case "MREC":
                            withComments = row.row.mrec_total_comments != 0;
                            break;
                        case "MCLI":
                            withComments = row.row.mcli_total_comments != 0;
                            break;
                        case "MTOD":
                            withComments =
                                row.row.msol_total_comments != 0 ||
                                row.row.mrev_total_comments != 0 ||
                                row.row.mpin_total_comments != 0 ||
                                row.row.maut_total_comments != 0 ||
                                row.row.mrec_total_comments != 0 ||
                                row.row.mcli_total_comments != 0;
                            break;
                    }

                    return (
                        <IconButton
                            disableFocusRipple
                            disableRipple
                            onClick={(event: React.SyntheticEvent<HTMLElement>) => handleOnClickLoanAppId(event, row)}
                        >
                            <img
                                onMouseOver={(e) => (e.currentTarget.src = WithComments)}
                                onMouseLeave={(e) => (e.currentTarget.src = withComments ? WithComments : CommentIcon)}
                                src={withComments ? WithComments : CommentIcon}
                            />
                        </IconButton>
                    );
                },
            },
            {
                headerName: "Detalle y Expediente",
                field: "dossier",
                align: "center",
                headerAlign: "center",
                flex: 1,
                hideSortIcons: true,
                disableColumnMenu: true,
                disableExport: true,
                disableReorder: true,
                sortable: false,
                renderCell: (row) => {
                    const companyName = row.row.company_source.toLowerCase();
                    return (
                        <Link to={`/solicitud/${companyName}/${row.row.id_application_company}`}>
                            <IconButton
                                disableFocusRipple
                                disableRipple
                                disabled={
                                    row.row.company_source !== "PYMIO" &&
                                    props.listCode == "MSOL" &&
                                    row.row["status_code"] === "CSCOF"
                                }
                                // onClick={(event: React.SyntheticEvent<HTMLElement>) => handleOnClickLoanAppId(event, row)}
                            >
                                <img
                                    onMouseOver={(e) => (e.currentTarget.src = SeeOverIcon)}
                                    onMouseLeave={(e) => (e.currentTarget.src = VisibilityIcon)}
                                    src={VisibilityIcon}
                                />
                            </IconButton>
                        </Link>
                    );
                },
            },
            {
                headerName: "Interés",
                field: "interested",
                align: "center",
                headerAlign: "center",
                flex: 1,
                hideSortIcons: true,
                disableColumnMenu: true,
                disableExport: true,
                disableReorder: true,
                renderCell: (row) => {
                    if (row.value === null) {
                        return (
                            <IconButton
                                disableFocusRipple
                                disableRipple
                                onClick={(event: React.SyntheticEvent<HTMLElement>) =>
                                    handleOnClickLoanAppId(event, row)
                                }
                            >
                                <img
                                    onMouseOver={(e) => (e.currentTarget.src = AlertPerson)}
                                    onMouseLeave={(e) => (e.currentTarget.src = Person)}
                                    src={Person}
                                    alt="Normal"
                                    title="Normal"
                                />
                            </IconButton>
                        );
                    } else if (row.value === false) {
                        return (
                            <IconButton
                                disableFocusRipple
                                disableRipple
                                onClick={(event: React.SyntheticEvent<HTMLElement>) =>
                                    handleOnClickLoanAppId(event, row)
                                }
                            >
                                <img
                                    onMouseOver={(e) => (e.currentTarget.src = RecoveryPerson)}
                                    onMouseLeave={(e) => (e.currentTarget.src = AlertPerson)}
                                    src={AlertPerson}
                                />
                            </IconButton>
                        );
                    } else if (row.value === true) {
                        return (
                            <IconButton
                                disableFocusRipple
                                disableRipple
                                onClick={(event: React.SyntheticEvent<HTMLElement>) =>
                                    handleOnClickLoanAppId(event, row)
                                }
                            >
                                <img
                                    onMouseOver={(e) => (e.currentTarget.src = AlertPerson)}
                                    onMouseLeave={(e) => (e.currentTarget.src = RecoveryPerson)}
                                    src={RecoveryPerson}
                                />
                            </IconButton>
                        );
                    }
                },
            },
            {
                headerName: "Relación Owner",
                field: "owner",
                align: "center",
                headerAlign: "center",
                flex: 1,
                hideSortIcons: true,
                disableColumnMenu: true,
                disableExport: true,
                disableReorder: true,
                sortable: false,
                renderCell: (row) => {
                    const ownerName = formatFullName(row.row.user_bpm);

                    return (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                                {ownerName || "--"}
                            </Typography>
                            {canAssignOwner && (
                                <IconButton
                                    size="small"
                                    disableFocusRipple
                                    disableRipple
                                    onClick={(event: React.SyntheticEvent<HTMLElement>) =>
                                        handleOnClickLoanAppId(event, row)
                                    }
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Box>
                    );
                },
            },
            {
                headerName: "Activa",
                field: "active",
                type: "boolean",
                align: "center",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                flex: 1,
                renderCell: (row) => {
                    return row.row.active ? "Si" : "No";
                },
            },
            {
                headerName: "Expira",
                field: "expire_at",
                type: "string",
                align: "center",
                headerAlign: "center",
                cellClassName: "centering-adjustment",
                flex: 0,
                renderCell: (row) => {
                    return dayjs(row.row.expire_at).isValid() ? dayjs(row.row.expire_at).format("YYYY-MM-DD") : "-";
                },
            },
        ];

        let columns = columnsDef.filter((tmp) => props.cols.some((col) => col === tmp.field));

        if (sbdexPermission === undefined || (sbdexPermission?.can_watch ?? false) === false) {
            columns = columns.filter((t) => t.field !== "dossier");
        }

        if (sbpinPermission === undefined || (sbpinPermission?.can_watch ?? false) === false) {
            columns = columns.filter((t) => t.field !== "interested");
        }

        if (sbcomPermission === undefined || (sbcomPermission?.can_watch ?? false) === false) {
            columns = columns.filter((t) => t.field !== "comments");
        }

        return columns;
    }, [props, canEditStatus]);

    const stageColors = new Map([
        ["Registro", "#818181"],
        ["Solicitud", "#32C0A9"],
        ["Autorizado", "#1D806F"],
        ["Rechazado", "#EE7777"],
        ["Expediente", "#528CD6"],
        ["Visita", "#894DD8"],
        ["Contratación", "#6D36B4"],
        ["Cliente", "#002652"],
    ]);

    const handleOnClickLoanAppId = async (event: React.SyntheticEvent<HTMLElement>, row: GridRenderCellParams) => {
        if (row.field === "id_application_company") {
            setChildrenElement(<LoanApplicationInfo data={row.row} />);
            setAnchorEl(event.currentTarget);
        } else if (row.field === "comments") {
            setChildrenElement(<LoanAppComments data={row.row} listCode={props.listCode} />);
            setAnchorEl(event.currentTarget);
        } else if (row.field === "interested") {
            if (row.value === false) {
                setChildrenElement(
                    <LoanAppChangeInterest
                        applicationId={row.row.id}
                        name={row.row.user_name}
                        onUpdateRow={handleOnUpdateRow}
                    />,
                );
            } else {
                setChildrenElement(<LoanAppLostInterest applicationId={row.row.id} onUpdateRow={handleOnUpdateRow} />);
            }

            setAnchorEl(event.currentTarget);
        } else if (row.field === "channel") {
            dispatch(setApplicationSelected(row.row));
            setChildrenElement(<LoanAppChannelInfo />);
            setAnchorEl(event.currentTarget);
        } else if (row.field === "owner") {
            setChildrenElement(
                <LoanAppOwnerAssignment
                    applicationId={row.row.id}
                    companyId={row.row.company_id}
                    stageId={row.row.stage.id}
                    currentOwnerName={formatFullName(row.row.user_bpm)}
                    onUpdateRow={handleOnUpdateRow}
                />,
            );
            setAnchorEl(event.currentTarget);
        } else if (row.field === "dossier") {
            const companyName = row.row.company_source.toLowerCase();

            const link = `/solicitud/${companyName}/${row.row.id_application_company}`;
            await navigate(link);
        }
    };

    const handleOnCellDoubleClick = async (params: GridCellParams) => {
        if (params.field !== "status_name") return;
        if (!canEditStatus) return;

        dispatch(setApplicationSelected(params.row));

        const updated = await dialogs.open(EdicionEstatusDialog, {
            productId: params.row.product_id,
            taxSystemId: params.row.tax_system.id,
            updateStore: false,
        });

        if (updated) {
            setRows((prevRows) => prevRows.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));
        }
    };

    const handleOnUpdateRow = (applicationId: UUID, attr: {}) => {
        setRows(
            rows.map((item) => {
                if (item.id === applicationId) {
                    item = { ...item, ...attr };
                }

                return item;
            }),
        );

        setAnchorEl(null);

        refetch();
    };

    const handleOnClosePopup = () => {
        setAnchorEl(null);
    };

    const downloadList = async () => {
        const data = await fetchPage(false);

        if (data !== undefined) {
            let headers = Object.keys(data.items[0]);

            headers = headers.filter((header) => getColumns.find((t) => t.field === header));
            let index = headers.findIndex((header) => header === "user_name");
            headers.splice(index + 1, 0, "user_phone");
            headers.splice(index + 2, 0, "user_email");

            let headersReplace = headers.map(
                (header) => getColumns.find((t) => t.field === header)?.headerName ?? header,
            );

            headersReplace = headersReplace.map((header) => (header == "user_phone" ? "Teléfono Usuario" : header));
            headersReplace = headersReplace.map((header) => (header == "user_email" ? "Correo Usuario" : header));

            const rows = data.items.map((item) => headers.map((header) => (item as any)[header]).join(","));

            const csvContent = [headersReplace.join(","), ...rows].join("\n");

            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = `solicitudes.csv`;
            link.click();
        }

        dispatch(setDownloadingList(false));
    };

    useEffect(() => {
        if (downloadingList === false) return;

        downloadList();
    }, [downloadingList]);

    return (
        <>
            <StripedDataGrid
                rows={rows}
                rowCount={configList?.rowCount ?? 0}
                filterMode="server"
                sortingMode="server"
                paginationMode="server"
                getRowHeight={(_) => "auto"}
                pageSizeOptions={[20, 40, 50, 100]}
                getRowId={(row) =>
                    companiesUser[0].name === "PYMIO" ? `${row.id}_${row.financial_company_id}` : row.id
                }
                columns={getColumns}
                sortModel={configList?.sort}
                onSortModelChange={(model) => dispatch(setSortsByCode({ code: props.listCode, sort: model }))}
                filterModel={configList?.filters ?? { items: [] }}
                onFilterModelChange={(model) => dispatch(setFiltersByCode({ code: props.listCode, filters: model }))}
                paginationModel={pagination}
                onPaginationModelChange={(model) => setPagination(model)}
                onCellDoubleClick={handleOnCellDoubleClick}
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                loading={isLoading}
                sx={{
                    borderTopLeftRadius: "0px !important",
                    borderTopRightRadius: "0px !important",
                }}
                getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd")}
                slotProps={{
                    loadingOverlay: {
                        variant: "linear-progress",
                    },
                }}
                autosizeOptions={{
                    expand: true,
                    includeHeaders: true,
                    includeOutliers: true,
                }}
            />
            <CustomPopupModal anchorEl={anchorEl} children={childrenElement} onClose={handleOnClosePopup} showClose />
        </>
    );
};
